import React, { useState, useEffect } from 'react';
import { usePracticeContent, PRACTICE_FALLBACK } from '../hooks/usePracticeContent';

const NAVY = '#002F6C';
const REDUCED =
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const H1_TEXT = 'studio 2+2 is a landscape architecture practice in Ahmedabad, India.';

function CountUp({ value, suffix, ready, delay = 0 }) {
  const [n, setN] = useState(REDUCED ? value : 0);
  useEffect(() => {
    if (REDUCED) { setN(value); return; }
    if (!ready) { setN(0); return; }
    let raf;
    const dur = 1400;
    setN(0);
    const run = () => {
      const start = performance.now();
      const tick = (t) => {
        const p = Math.min(1, (t - start) / dur);
        const eased = 1 - Math.pow(1 - p, 3);
        setN(Math.round(eased * value));
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    };
    const timer = setTimeout(run, delay);
    return () => { clearTimeout(timer); if (raf) cancelAnimationFrame(raf); };
  }, [ready, value, delay]);
  return (
    <>
      {n.toLocaleString('en-US')}
      {suffix}
    </>
  );
}

const Stat = ({ label, value, suffix, ready, delay, testid }) => (
  <div className="ph-stat flex flex-col" data-testid={testid}>
    <span
      className="leading-none whitespace-nowrap"
      style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, color: NAVY, fontSize: 'clamp(56px, 7.6vw, 120px)', fontVariantNumeric: 'tabular-nums' }}
    >
      <CountUp value={value} suffix={suffix} ready={ready} delay={delay} />
    </span>
    <span
      className="mt-3 uppercase"
      style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '11.5px', letterSpacing: '0.28em', color: '#3A3530' }}
    >
      {label}
    </span>
  </div>
);

const PracticeHero = () => {
  const { data, loading } = usePracticeContent();
  const hero = (data && data.hero) || PRACTICE_FALLBACK.hero;
  const ready = !loading;
  const words = H1_TEXT.split(' ');

  return (
    <section
      className="relative flex flex-col justify-center"
      style={{ minHeight: '100vh', backgroundColor: '#F8F8F7' }}
      data-testid="practice-hero"
    >
      <style>{`
        @keyframes practiceWordRise {
          from { opacity: 0; transform: translateY(0.5em); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .ph-word {
          display: inline-block;
          opacity: 0;
          animation: practiceWordRise 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
        }
        @keyframes practiceDrip {
          0%   { transform: translateY(-100%); }
          100% { transform: translateY(220%); }
        }
        .ph-drip-dot { animation: practiceDrip 2.2s ease-in-out infinite; }
        @keyframes practiceTaglineDrift {
          0%   { transform: translateY(0); }
          50%  { transform: translateY(-7px); }
          100% { transform: translateY(0); }
        }
        .ph-tagline { animation: practiceTaglineDrift 10s ease-in-out infinite; will-change: transform; }
        .ph-stats{ display:flex; align-items:flex-end; flex-wrap:nowrap; }
        .ph-stat{ padding:0 clamp(20px,2.6vw,40px); border-left:1px solid rgba(58,53,48,0.18); }
        .ph-stat:first-child{ padding-left:0; border-left:none; }
        .ph-stat:last-child{ padding-right:0; }
        @media (max-width:640px){
          .ph-stats{ flex-direction:column; align-items:flex-start; gap:34px; }
          .ph-stat{ padding:0; border-left:none; }
        }
        @media (prefers-reduced-motion: reduce) {
          .ph-word { opacity: 1; animation: none; }
          .ph-drip-dot { animation: none; }
          .ph-tagline { animation: none; transform: none; will-change: auto; }
        }
      `}</style>

      <div className="pr-spine py-28">
        <p
          className="mb-8 uppercase"
          style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '11.5px', letterSpacing: '0.3em', color: '#4A6A9C' }}
          data-testid="practice-hero-kicker"
        >
          our practice
        </p>

        <h1
          className="lowercase"
          style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, color: '#3A3530', fontSize: 'clamp(34px, 6vw, 76px)', lineHeight: 1.08, letterSpacing: '-0.01em', maxWidth: '18ch' }}
          data-testid="practice-hero-h1"
        >
          {words.map((w, i) => (
            <span
              key={i}
              className="ph-word"
              style={{ animationDelay: REDUCED ? '0ms' : `${i * 90}ms` }}
            >
              {w}
              {i < words.length - 1 ? '\u00A0' : ''}
            </span>
          ))}
        </h1>

        <p
          className="mt-6 italic ph-tagline"
          style={{ fontFamily: '"Cormorant Garamond", serif', fontWeight: 500, color: NAVY, fontSize: 'clamp(20px, 3vw, 34px)' }}
          data-testid="practice-hero-tagline"
        >
          taking care of your natural affairs.
        </p>

        <div className="ph-stats mt-16 md:mt-24">
          <Stat label="projects" value={hero.projects.value} suffix={hero.projects.suffix} ready={ready} delay={0} testid="practice-hero-stat-projects" />
          <Stat label="cities" value={hero.cities.value} suffix={hero.cities.suffix} ready={ready} delay={120} testid="practice-hero-stat-cities" />
          <Stat label="years" value={hero.years.value} suffix={hero.years.suffix} ready={ready} delay={240} testid="practice-hero-stat-years" />
        </div>
      </div>

      <div className="absolute left-0 right-0 bottom-10" data-testid="practice-hero-scroll">
        <div className="pr-spine flex items-center gap-3">
          <span
            className="uppercase"
            style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '10.5px', letterSpacing: '0.3em', color: '#4A6A9C' }}
          >
            scroll
          </span>
          <span className="relative block w-px h-12 overflow-hidden" style={{ backgroundColor: 'rgba(0,47,108,0.2)' }}>
            <span className="ph-drip-dot absolute left-0 top-0 w-px h-4" style={{ backgroundColor: NAVY }} />
          </span>
        </div>
      </div>
    </section>
  );
};

export default PracticeHero;
